import { collection, getDocs, addDoc, updateDoc, doc } from "firebase/firestore";
import { db } from "@/firebase/config";

/**
 * Enhanced Chatbot Service
 * Handles common questions, fallback responses, and dynamic data management
 */
class EnhancedChatbotService {
  constructor() {
    this.menuItems = [];
    this.restaurantInfo = {};
    this.commonQuestions = [];
    this.generalQuestions = [];
    this.currentLanguage = "en";
    this.initializeData();
  }

  /**
   * Set current language
   */
  setLanguage(lang) {
    this.currentLanguage = lang;
    console.log("Enhanced chatbot language set to:", lang);
  }

  /**
   * Get current language
   */
  getCurrentLanguage() {
    return this.currentLanguage;
  }

  /**
   * Initialize data from Firebase
   */
  async initializeData() {
    try {
      // Load menu items
      const menuSnapshot = await getDocs(collection(db, "menu_items"));
      this.menuItems = menuSnapshot.docs.map((doc) => doc.data());

      // Load restaurant info
      const infoSnapshot = await getDocs(collection(db, "restaurant_info"));
      this.restaurantInfo = infoSnapshot.docs[0]?.data() || {};

      // Load common questions
      const questionsSnapshot = await getDocs(collection(db, "common_questions"));
      this.commonQuestions = questionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Load general questions
      const generalQuestionsSnapshot = await getDocs(collection(db, "questions"));
      this.generalQuestions = generalQuestionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Enhanced chatbot data loaded:", {
        menuItems: this.menuItems.length,
        restaurantInfo: !!this.restaurantInfo,
        commonQuestions: this.commonQuestions.length,
        generalQuestions: this.generalQuestions.length,
      });
    } catch (error) {
      console.error("Error loading enhanced chatbot data:", error);
    }
  }

  /**
   * Find meal by name (case-insensitive) - Enhanced with comprehensive meal matching
   */
  findMealByName(mealName) {
    const searchName = mealName.toLowerCase().trim();
    
    // First try exact match
    let meal = this.menuItems.find((item) => 
      item.name.toLowerCase() === searchName
    );
    
    if (meal) return meal;
    
    // Try partial match
    meal = this.menuItems.find((item) => 
      item.name.toLowerCase().includes(searchName) ||
      searchName.includes(item.name.toLowerCase())
    );
    
    if (meal) return meal;
    
    // Try matching with common meal variations and aliases
    const mealAliases = {
      // Sushi variations
      "sushi": "Dragon Sushi",
      "سوشي": "Dragon Sushi",
      "dragon": "Dragon Sushi",
      "dragon sushi": "Dragon Sushi",
      "creamy": "Creamy Sushi",
      "creamy sushi": "Creamy Sushi",
      "salmon": "Salmon Roll",
      "salmon roll": "Salmon Roll",
      "salmon sushi": "Salmon Roll",
      
      // Steak variations
      "steak": "Steak Tartare",
      "ستيك": "Steak Tartare",
      "beef": "Wagyu Beef",
      "لحم": "Wagyu Beef",
      "wagyu": "Wagyu Beef",
      "wagyu beef": "Wagyu Beef",
      "tartare": "Steak Tartare",
      "steak tartare": "Steak Tartare",
      "surf": "Surf & Turf",
      "surf turf": "Surf & Turf",
      "surf and turf": "Surf & Turf",
      
      // Chicken variations
      "chicken": "Creamy Parmesan Chicken",
      "دجاج": "Creamy Parmesan Chicken",
      "parmesan": "Creamy Parmesan Chicken",
      "creamy parmesan": "Creamy Parmesan Chicken",
      "creamy parmesan chicken": "Creamy Parmesan Chicken",
      "spring": "Spring Chicken",
      "spring chicken": "Spring Chicken",
      "francese": "Chicken Francese",
      "chicken francese": "Chicken Francese",
      
      // Appetizer variations
      "appetizer": "Tomato Toast",
      "مقبلات": "Tomato Toast",
      "starter": "Tomato Toast",
      "tomato": "Tomato Toast",
      "tomato toast": "Tomato Toast",
      "toast": "Tomato Toast",
      "noodle": "Noodle Soup",
      "noodle soup": "Noodle Soup",
      "soup": "Noodle Soup",
      "شوربة": "Noodle Soup",
      "pumpkin": "Pumpkin Soup",
      "pumpkin soup": "Pumpkin Soup",
      "قرع": "Pumpkin Soup",
      
      // Beverage variations
      "drink": "Panthouse Tonic",
      "مشروب": "Panthouse Tonic",
      "beverage": "Panthouse Tonic",
      "tonic": "Panthouse Tonic",
      "panthouse": "Panthouse Tonic",
      "panthouse tonic": "Panthouse Tonic",
      "apple": "Apple Breeze",
      "apple breeze": "Apple Breeze",
      "breeze": "Apple Breeze",
      "frenchman": "Frenchman Blitz",
      "frenchman blitz": "Frenchman Blitz",
      "blitz": "Frenchman Blitz"
    };
    
    const alias = mealAliases[searchName];
    if (alias) {
      return this.menuItems.find((item) => item.name === alias);
    }
    
    return null;
  }

  /**
   * Check if user is asking about a specific category (chicken, steak, appetizers, etc.)
   */
  isCategoryQuery(userMessage) {
    const lowerMessage = userMessage.toLowerCase();
    const categoryKeywords = {
      chicken: ["chicken", "دجاج", "فراخ"],
      steak: ["steak", "ستيك", "لحم", "beef", "لحم بقري"],
      appetizer: ["appetizer", "مقبلات", "starter", "starters", "appetizers"],
      sushi: ["sushi", "سوشي", "japanese", "ياباني"],
      beverage: ["beverage", "مشروب", "drink", "drinks", "مشروبات", "beverages"],
      soup: ["soup", "شوربة", "شوربات", "soups"]
    };
    
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some(keyword => lowerMessage.includes(keyword))) {
        return category;
      }
    }
    
    return null;
  }

    /**
   * Generate comprehensive category response with all items
   */
   generateCategoryResponse(category) {
     const categoryItems = this.getCategoryItems(category);
     
     if (this.currentLanguage === "ar") {
       let response = `🍽️ **${this.getCategoryTitle(category, "ar")}** 🍽️\n\n`;
       
       categoryItems.forEach((item, index) => {
         const arabicName = this.getArabicDishName(item.name);
         const description = item.description_ar || item.description;
         response += `${index + 1}. **${arabicName}** (${item.price} جنيه)\n`;
         response += `   📝 ${description}\n\n`;
       });
       
       response += `💡 **أي من هذه الأطباق تريد معرفة المزيد عنه؟** 🍽️`;
       
       return response;
     } else {
       let response = `🍽️ **${this.getCategoryTitle(category, "en")}** 🍽️\n\n`;
       
       categoryItems.forEach((item, index) => {
         response += `${index + 1}. **${item.name}** (${item.price} LE)\n`;
         response += `   📝 ${item.description}\n\n`;
       });
       
       response += `💡 **Which of these dishes would you like to know more about?** 🍽️`;
       
       return response;
     }
   }

  /**
   * Get Arabic dish name translation
   */
  getArabicDishName(englishName) {
    const dishTranslations = {
      // Sushi dishes
      "Dragon Sushi": "سوشي التنين",
      "Creamy Sushi": "سوشي كريمي",
      "Salmon Roll": "رول السلمون",
      
      // Steak dishes
      "Steak Tartare": "ستيك تارتار",
      "Surf & Turf": "سيرف آند تيرف",
      "Wagyu Beef": "لحم واجيو",
      
      // Chicken dishes
      "Creamy Parmesan Chicken": "دجاج بارميزان كريمي",
      "Spring Chicken": "دجاج الربيع",
      "Chicken Francese": "دجاج فرانسيس",
      
      // Appetizer dishes
      "Tomato Toast": "توست الطماطم",
      "Noodle Soup": "شوربة النودلز",
      "Pumpkin Soup": "شوربة القرع",
      
      // Beverage dishes
      "Panthouse Tonic": "تونيك بانثوس",
      "Apple Breeze": "نسيم التفاح",
      "Frenchman Blitz": "بليتز الفرنسي"
    };
    
    return dishTranslations[englishName] || englishName;
  }

  /**
   * Get category title in specified language
   */
  getCategoryTitle(category, language) {
    const titles = {
      chicken: {
        en: "Our Chicken Dishes",
        ar: "أطباق الدجاج لدينا"
      },
      steak: {
        en: "Our Steak & Beef Dishes",
        ar: "أطباق الستيك واللحم البقري لدينا"
      },
      appetizer: {
        en: "Our Appetizers & Starters",
        ar: "مقبلاتنا وبداياتنا"
      },
      sushi: {
        en: "Our Sushi & Japanese Dishes",
        ar: "أطباق السوشي واليابانية لدينا"
      },
      beverage: {
        en: "Our Beverages & Drinks",
        ar: "مشروباتنا"
      },
      soup: {
        en: "Our Soups",
        ar: "شورباتنا"
      }
    };
    
    return titles[category]?.[language] || titles[category]?.en || category;
  }

  /**
   * Get all items for a specific category
   */
  getCategoryItems(category) {
    const categoryMapping = {
      chicken: ["Creamy Parmesan Chicken", "Spring Chicken", "Chicken Francese"],
      steak: ["Steak Tartare", "Surf & Turf", "Wagyu Beef"],
      appetizer: ["Tomato Toast", "Noodle Soup", "Pumpkin Soup"],
      sushi: ["Dragon Sushi", "Creamy Sushi", "Salmon Roll"],
      beverage: ["Panthouse Tonic", "Apple Breeze", "Frenchman Blitz"],
      soup: ["Noodle Soup", "Pumpkin Soup"]
    };
    
    const itemNames = categoryMapping[category] || [];
    return itemNames.map(name => this.getMealInfo(name)).filter(Boolean);
  }

  /**
   * Find meals containing a specific ingredient
   */
  findMealsByIngredient(ingredient) {
    const searchIngredient = ingredient.toLowerCase().trim();
    const matchingMeals = [];

    for (const meal of this.menuItems) {
      const ingredients = this.generateIngredients(meal);
      const hasIngredient = ingredients.some(ing => 
        ing.toLowerCase().includes(searchIngredient) ||
        searchIngredient.includes(ing.toLowerCase())
      );
      
      if (hasIngredient) {
        matchingMeals.push(meal);
      }
    }

    return matchingMeals;
  }

  /**
   * Check if user is asking about ingredients
   */
  isIngredientQuery(userMessage) {
    const ingredientKeywords = [
      "contains", "has", "with", "ingredient", "made of", "includes",
      "يحتوي على", "به", "مع", "مكون", "مصنوع من", "يشمل"
    ];

    const lowerMessage = userMessage.toLowerCase();
    return ingredientKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  /**
   * Extract ingredient from user message
   */
  extractIngredient(userMessage) {
    const lowerMessage = userMessage.toLowerCase();
    
    // Common ingredients to look for
    const commonIngredients = [
      "parmesan", "cheese", "chicken", "beef", "salmon", "avocado", "tomato",
      "onion", "garlic", "cream", "butter", "olive oil", "lemon", "herbs",
      "بارميزان", "جبن", "دجاج", "لحم بقري", "سلمون", "أفوكادو", "طماطم",
      "بصل", "ثوم", "كريمة", "زبدة", "زيت زيتون", "ليمون", "أعشاب"
    ];

    for (const ingredient of commonIngredients) {
      if (lowerMessage.includes(ingredient)) {
        return ingredient;
      }
    }

    // If no common ingredient found, try to extract from the message
    const words = lowerMessage.split(/\s+/);
    const ingredientWords = words.filter(word => 
      word.length > 2 && 
      !["the", "and", "or", "but", "for", "with", "has", "have", "contains", "ال", "في", "من", "إلى", "على", "مع", "له", "لها"].includes(word)
    );

    return ingredientWords[ingredientWords.length - 1] || null;
  }

  /**
   * Generate response for ingredient-based queries
   */
  generateIngredientResponse(ingredient, matchingMeals) {
    if (matchingMeals.length === 0) {
      if (this.currentLanguage === "ar") {
        return `عذراً، لا نجد أطباق تحتوي على "${ingredient}" في قائمة طعامنا الحالية. يمكنك تصفح قائمة الطعام الكاملة أو سؤالنا عن مكونات أخرى! 🍽️`;
      }
      return `Sorry, we don't have any dishes containing "${ingredient}" in our current menu. You can browse our full menu or ask about other ingredients! 🍽️`;
    }

    if (this.currentLanguage === "ar") {
      let response = `🍽️ **الأطباق التي تحتوي على "${ingredient}":**\n\n`;
      
      matchingMeals.forEach((meal, index) => {
        const arabicName = this.getArabicDishName(meal.name);
        response += `${index + 1}. **${arabicName}** (${meal.price} جنيه)\n`;
        response += `   📝 ${meal.description}\n\n`;
      });
      
      response += `💡 **هل تريد تجربة أحد هذه الأطباق أو مشروباتنا المنعشة؟** 🍹`;
      
      return response;
    } else {
      let response = `🍽️ **Dishes containing "${ingredient}":**\n\n`;
      
      matchingMeals.forEach((meal, index) => {
        response += `${index + 1}. **${meal.name}** (${meal.price} LE)\n`;
        response += `   📝 ${meal.description}\n\n`;
      });
      
      response += `💡 **Would you like to try one of these dishes or our refreshing drinks?** 🍹`;
      
      return response;
    }
  }

    /**
   * Generate meal-specific response with description, ingredients, and contextual follow-up questions
   */
   generateMealResponse(meal) {
     if (!meal) return null;

     // Generate ingredients based on meal category and name
     const ingredients = this.generateIngredients(meal);
     
     if (this.currentLanguage === "ar") {
       const arabicName = this.getArabicDishName(meal.name);
       const description = meal.description_ar || meal.description;
       let response = `🍽️ **${arabicName}** (${meal.price} جنيه)\n\n`;
       response += `📝 **الوصف:** ${description}\n\n`;
       
       if (ingredients.length > 0) {
         response += `🥘 **المكونات:** ${ingredients.join("، ")}\n\n`;
       }
       
       // Contextual follow-up questions based on meal category
       const followUpQuestions = this.getFollowUpQuestions(meal.category, "ar");
       response += `💡 **${followUpQuestions.join(" • ")}** 🍽️`;
       
       return response;
     } else {
       let response = `🍽️ **${meal.name}** (${meal.price} LE)\n\n`;
       response += `📝 **Description:** ${meal.description}\n\n`;
       
       if (ingredients.length > 0) {
         response += `🥘 **Ingredients:** ${ingredients.join(", ")}\n\n`;
       }
       
       // Contextual follow-up questions based on meal category
       const followUpQuestions = this.getFollowUpQuestions(meal.category, "en");
       response += `💡 **${followUpQuestions.join(" • ")}** 🍽️`;
       
       return response;
     }
   }

  /**
   * Get contextual follow-up questions based on meal category
   */
  getFollowUpQuestions(category, language) {
    const followUps = {
      sushi: {
        en: [
          "Want to try our steak dishes?",
          "How about refreshing beverages?",
          "Ready to place an order?"
        ],
        ar: [
          "تريد تجربة أطباق الستيك؟",
          "ماذا عن المشروبات المنعشة؟",
          "مستعد لطلب الطعام؟"
        ]
      },
      steak: {
        en: [
          "Interested in our sushi selection?",
          "Want to see our appetizers?",
          "Ready to make a reservation?"
        ],
        ar: [
          "مهتم باختيار السوشي؟",
          "تريد رؤية المقبلات؟",
          "مستعد للحجز؟"
        ]
      },
      chicken: {
        en: [
          "Want to explore our seafood options?",
          "How about our signature drinks?",
          "Ready to order?"
        ],
        ar: [
          "تريد استكشاف خيارات المأكولات البحرية؟",
          "ماذا عن مشروباتنا المميزة؟",
          "مستعد للطلب؟"
        ]
      },
      appetizer: {
        en: [
          "Want to see our main courses?",
          "How about our dessert menu?",
          "Ready to place your order?"
        ],
        ar: [
          "تريد رؤية الأطباق الرئيسية؟",
          "ماذا عن قائمة الحلويات؟",
          "مستعد لطلب الطعام؟"
        ]
      },
      beverage: {
        en: [
          "Want to see our food menu?",
          "How about our appetizers?",
          "Ready to make a reservation?"
        ],
        ar: [
          "تريد رؤية قائمة الطعام؟",
          "ماذا عن المقبلات؟",
          "مستعد للحجز؟"
        ]
      }
    };
    
    const categoryFollowUps = followUps[category] || followUps.appetizer;
    return categoryFollowUps[language] || categoryFollowUps.en;
  }

    /**
   * Get comprehensive meal information for any meal name
   */
   getMealInfo(mealName) {
     const mealDatabase = {
       // Sushi Category - All variations covered
       "dragon sushi": {
         name: "Dragon Sushi",
         price: "120 LE",
         description: "Fresh salmon with avocado and spicy mayo",
         description_ar: "سلمون طازج مع أفوكادو ومايونيز حار",
         category: "sushi",
         ingredients: {
           en: ["Fresh salmon", "Avocado", "Cucumber", "Spicy mayo", "Eel sauce"],
           ar: ["سلمون طازج", "أفوكادو", "خيار", "مايونيز حار", "صلصة الإيل"]
         }
       },
             "dragon": {
         name: "Dragon Sushi",
         price: "120 LE",
         description: "Fresh salmon with avocado and spicy mayo",
         description_ar: "سلمون طازج مع أفوكادو ومايونيز حار",
         category: "sushi",
         ingredients: {
           en: ["Fresh salmon", "Avocado", "Cucumber", "Spicy mayo", "Eel sauce"],
           ar: ["سلمون طازج", "أفوكادو", "خيار", "مايونيز حار", "صلصة الإيل"]
         }
       },
       "creamy sushi": {
         name: "Creamy Sushi",
         price: "110 LE",
         description: "Sushi rice with creamy cheese sauce",
         description_ar: "أرز سوشي مع صلصة جبن كريمية",
         category: "sushi",
         ingredients: {
           en: ["Sushi rice", "Cream cheese", "Fresh vegetables", "Creamy sauce"],
           ar: ["أرز سوشي", "كريمة جبن", "خضروات طازجة", "صلصة كريمية"]
         }
       },
       "creamy": {
         name: "Creamy Sushi",
         price: "110 LE",
         description: "Sushi rice with creamy cheese sauce",
         description_ar: "أرز سوشي مع صلصة جبن كريمية",
         category: "sushi",
         ingredients: {
           en: ["Sushi rice", "Cream cheese", "Fresh vegetables", "Creamy sauce"],
           ar: ["أرز سوشي", "كريمة جبن", "خضروات طازجة", "صلصة كريمية"]
         }
       },
       "salmon roll": {
         name: "Salmon Roll",
         price: "115 LE",
         description: "Fresh salmon with sushi rice and nori",
         description_ar: "سلمون طازج مع أرز سوشي ونوري",
         category: "sushi",
         ingredients: {
           en: ["Fresh salmon", "Sushi rice", "Nori", "Vegetables"],
           ar: ["سلمون طازج", "أرز سوشي", "نوري", "خضروات"]
         }
       },
       "salmon": {
         name: "Salmon Roll",
         price: "115 LE",
         description: "Fresh salmon with sushi rice and nori",
         description_ar: "سلمون طازج مع أرز سوشي ونوري",
         category: "sushi",
         ingredients: {
           en: ["Fresh salmon", "Sushi rice", "Nori", "Vegetables"],
           ar: ["سلمون طازج", "أرز سوشي", "نوري", "خضروات"]
         }
       },
      
             // Steak Category - All variations covered
       "steak tartare": {
         name: "Steak Tartare",
         price: "180 LE",
         description: "Premium beef with capers and Dijon mustard",
         description_ar: "لحم بقري فاخر مع كبر وخردل ديجون",
         category: "steak",
         ingredients: {
           en: ["Premium beef", "Capers", "Onion", "Dijon mustard", "Spices"],
           ar: ["لحم بقري فاخر", "كبر", "بصل", "خردل ديجون", "توابل"]
         }
       },
       "tartare": {
         name: "Steak Tartare",
         price: "180 LE",
         description: "Premium beef with capers and Dijon mustard",
         description_ar: "لحم بقري فاخر مع كبر وخردل ديجون",
         category: "steak",
         ingredients: {
           en: ["Premium beef", "Capers", "Onion", "Dijon mustard", "Spices"],
           ar: ["لحم بقري فاخر", "كبر", "بصل", "خردل ديجون", "توابل"]
         }
       },
       "surf & turf": {
         name: "Surf & Turf",
         price: "220 LE",
         description: "Beef and seafood combination with grilled vegetables",
         description_ar: "مزيج من اللحم البقري والمأكولات البحرية مع خضروات مشوية",
         category: "steak",
         ingredients: {
           en: ["Beef", "Seafood", "Grilled vegetables", "Special sauce"],
           ar: ["لحم بقري", "مأكولات بحرية", "خضروات مشوية", "صلصة خاصة"]
         }
       },
       "surf turf": {
         name: "Surf & Turf",
         price: "220 LE",
         description: "Beef and seafood combination with grilled vegetables",
         description_ar: "مزيج من اللحم البقري والمأكولات البحرية مع خضروات مشوية",
         category: "steak",
         ingredients: {
           en: ["Beef", "Seafood", "Grilled vegetables", "Special sauce"],
           ar: ["لحم بقري", "مأكولات بحرية", "خضروات مشوية", "صلصة خاصة"]
         }
       },
       "surf": {
         name: "Surf & Turf",
         price: "220 LE",
         description: "Beef and seafood combination with grilled vegetables",
         description_ar: "مزيج من اللحم البقري والمأكولات البحرية مع خضروات مشوية",
         category: "steak",
         ingredients: {
           en: ["Beef", "Seafood", "Grilled vegetables", "Special sauce"],
           ar: ["لحم بقري", "مأكولات بحرية", "خضروات مشوية", "صلصة خاصة"]
         }
       },
       "wagyu beef": {
         name: "Wagyu Beef",
         price: "280 LE",
         description: "Japanese Wagyu beef with sea salt and black pepper",
         description_ar: "لحم واجيو ياباني مع ملح البحر وفلفل أسود",
         category: "steak",
         ingredients: {
           en: ["Japanese Wagyu beef", "Sea salt", "Black pepper", "Olive oil"],
           ar: ["لحم واجيو ياباني", "ملح البحر", "فلفل أسود", "زيت الزيتون"]
         }
       },
       "wagyu": {
         name: "Wagyu Beef",
         price: "280 LE",
         description: "Japanese Wagyu beef with sea salt and black pepper",
         description_ar: "لحم واجيو ياباني مع ملح البحر وفلفل أسود",
         category: "steak",
         ingredients: {
           en: ["Japanese Wagyu beef", "Sea salt", "Black pepper", "Olive oil"],
           ar: ["لحم واجيو ياباني", "ملح البحر", "فلفل أسود", "زيت الزيتون"]
         }
       },
      
             // Chicken Category - All variations covered
       "creamy parmesan chicken": {
         name: "Creamy Parmesan Chicken",
         price: "140 LE",
         description: "Tender chicken with parmesan cheese and cream sauce",
         description_ar: "دجاج طري مع جبن بارميزان وصلصة كريمية",
         category: "chicken",
         ingredients: {
           en: ["Tender chicken", "Parmesan cheese", "Cream", "Grilled vegetables"],
           ar: ["دجاج طري", "جبن بارميزان", "كريمة", "خضروات مشوية"]
         }
       },
       "creamy parmesan": {
         name: "Creamy Parmesan Chicken",
         price: "140 LE",
         description: "Tender chicken with parmesan cheese and cream sauce",
         description_ar: "دجاج طري مع جبن بارميزان وصلصة كريمية",
         category: "chicken",
         ingredients: {
           en: ["Tender chicken", "Parmesan cheese", "Cream", "Grilled vegetables"],
           ar: ["دجاج طري", "جبن بارميزان", "كريمة", "خضروات مشوية"]
         }
       },
       "parmesan": {
         name: "Creamy Parmesan Chicken",
         price: "140 LE",
         description: "Tender chicken with parmesan cheese and cream sauce",
         description_ar: "دجاج طري مع جبن بارميزان وصلصة كريمية",
         category: "chicken",
         ingredients: {
           en: ["Tender chicken", "Parmesan cheese", "Cream", "Grilled vegetables"],
           ar: ["دجاج طري", "جبن بارميزان", "كريمة", "خضروات مشوية"]
         }
       },
       "spring chicken": {
         name: "Spring Chicken",
         price: "130 LE",
         description: "Fresh chicken with spring vegetables and herbs",
         description_ar: "دجاج طازج مع خضروات الربيع وأعشاب طازجة",
         category: "chicken",
         ingredients: {
           en: ["Fresh chicken", "Spring vegetables", "Fresh herbs", "Light sauce"],
           ar: ["دجاج طازج", "خضروات الربيع", "أعشاب طازجة", "صلصة خفيفة"]
         }
       },
       "spring": {
         name: "Spring Chicken",
         price: "130 LE",
         description: "Fresh chicken with spring vegetables and herbs",
         description_ar: "دجاج طازج مع خضروات الربيع وأعشاب طازجة",
         category: "chicken",
         ingredients: {
           en: ["Fresh chicken", "Spring vegetables", "Fresh herbs", "Light sauce"],
           ar: ["دجاج طازج", "خضروات الربيع", "أعشاب طازجة", "صلصة خفيفة"]
         }
       },
       "chicken francese": {
         name: "Chicken Francese",
         price: "135 LE",
         description: "Chicken with lemon and French herbs",
         description_ar: "دجاج مع ليمون وأعشاب فرنسية",
         category: "chicken",
         ingredients: {
           en: ["Chicken", "Lemon", "Olive oil", "French herbs"],
           ar: ["دجاج", "ليمون", "زيت الزيتون", "أعشاب فرنسية"]
         }
       },
       "francese": {
         name: "Chicken Francese",
         price: "135 LE",
         description: "Chicken with lemon and French herbs",
         description_ar: "دجاج مع ليمون وأعشاب فرنسية",
         category: "chicken",
         ingredients: {
           en: ["Chicken", "Lemon", "Olive oil", "French herbs"],
           ar: ["دجاج", "ليمون", "زيت الزيتون", "أعشاب فرنسية"]
         }
       },
      
             // Appetizer Category - All variations covered
       "tomato toast": {
         name: "Tomato Toast",
         price: "45 LE",
         description: "Fresh tomatoes on artisan bread with herbs",
         description_ar: "طماطم طازجة على خبز أرتيزان مع أعشاب",
         category: "appetizer",
         ingredients: {
           en: ["Fresh tomatoes", "Artisan bread", "Herbs", "Olive oil"],
           ar: ["طماطم طازجة", "خبز أرتيزان", "أعشاب", "زيت الزيتون"]
         }
       },
       "tomato": {
         name: "Tomato Toast",
         price: "45 LE",
         description: "Fresh tomatoes on artisan bread with herbs",
         description_ar: "طماطم طازجة على خبز أرتيزان مع أعشاب",
         category: "appetizer",
         ingredients: {
           en: ["Fresh tomatoes", "Artisan bread", "Herbs", "Olive oil"],
           ar: ["طماطم طازجة", "خبز أرتيزان", "أعشاب", "زيت الزيتون"]
         }
       },
       "toast": {
         name: "Tomato Toast",
         price: "45 LE",
         description: "Fresh tomatoes on artisan bread with herbs",
         description_ar: "طماطم طازجة على خبز أرتيزان مع أعشاب",
         category: "appetizer",
         ingredients: {
           en: ["Fresh tomatoes", "Artisan bread", "Herbs", "Olive oil"],
           ar: ["طماطم طازجة", "خبز أرتيزان", "أعشاب", "زيت الزيتون"]
         }
       },
       "noodle soup": {
         name: "Noodle Soup",
         price: "55 LE",
         description: "Asian noodles with vegetables and chicken broth",
         description_ar: "نودلز آسيوية مع خضروات ومرق دجاج",
         category: "appetizer",
         ingredients: {
           en: ["Asian noodles", "Vegetables", "Chicken broth", "Asian spices"],
           ar: ["نودلز آسيوية", "خضروات", "مرق دجاج", "توابل آسيوية"]
         }
       },
       "noodle": {
         name: "Noodle Soup",
         price: "55 LE",
         description: "Asian noodles with vegetables and chicken broth",
         description_ar: "نودلز آسيوية مع خضروات ومرق دجاج",
         category: "appetizer",
         ingredients: {
           en: ["Asian noodles", "Vegetables", "Chicken broth", "Asian spices"],
           ar: ["نودلز آسيوية", "خضروات", "مرق دجاج", "توابل آسيوية"]
         }
       },
       "pumpkin soup": {
         name: "Pumpkin Soup",
         price: "50 LE",
         description: "Fresh pumpkin with cream and seasonal spices",
         description_ar: "قرع طازج مع كريمة وتوابل موسمية",
         category: "appetizer",
         ingredients: {
           en: ["Fresh pumpkin", "Cream", "Seasonal spices", "Vegetables"],
           ar: ["قرع طازج", "كريمة", "توابل موسمية", "خضروات"]
         }
       },
       "pumpkin": {
         name: "Pumpkin Soup",
         price: "50 LE",
         description: "Fresh pumpkin with cream and seasonal spices",
         description_ar: "قرع طازج مع كريمة وتوابل موسمية",
         category: "appetizer",
         ingredients: {
           en: ["Fresh pumpkin", "Cream", "Seasonal spices", "Vegetables"],
           ar: ["قرع طازج", "كريمة", "توابل موسمية", "خضروات"]
         }
       },
      
             // Beverage Category - All variations covered
       "panthouse tonic": {
         name: "Panthouse Tonic",
         price: "35 LE",
         description: "Refreshing tonic with lemon and fresh herbs",
         description_ar: "تونيك منعش مع ليمون وأعشاب طازجة",
         category: "beverage",
         ingredients: {
           en: ["Tonic", "Lemon", "Lime", "Fresh herbs"],
           ar: ["تونيك", "ليمون", "ليمون حامض", "أعشاب طازجة"]
         }
       },
       "panthouse": {
         name: "Panthouse Tonic",
         price: "35 LE",
         description: "Refreshing tonic with lemon and fresh herbs",
         description_ar: "تونيك منعش مع ليمون وأعشاب طازجة",
         category: "beverage",
         ingredients: {
           en: ["Tonic", "Lemon", "Lime", "Fresh herbs"],
           ar: ["تونيك", "ليمون", "ليمون حامض", "أعشاب طازجة"]
         }
       },
       "tonic": {
         name: "Panthouse Tonic",
         price: "35 LE",
         description: "Refreshing tonic with lemon and fresh herbs",
         description_ar: "تونيك منعش مع ليمون وأعشاب طازجة",
         category: "beverage",
         ingredients: {
           en: ["Tonic", "Lemon", "Lime", "Fresh herbs"],
           ar: ["تونيك", "ليمون", "ليمون حامض", "أعشاب طازجة"]
         }
       },
       "apple breeze": {
         name: "Apple Breeze",
         price: "30 LE",
         description: "Fresh apple juice with mint and lemon",
         description_ar: "عصير تفاح طازج مع نعناع وليمون",
         category: "beverage",
         ingredients: {
           en: ["Fresh apple juice", "Mint", "Lemon", "Ice"],
           ar: ["عصير تفاح طازج", "نعناع", "ليمون", "ثلج"]
         }
       },
       "apple": {
         name: "Apple Breeze",
         price: "30 LE",
         description: "Fresh apple juice with mint and lemon",
         description_ar: "عصير تفاح طازج مع نعناع وليمون",
         category: "beverage",
         ingredients: {
           en: ["Fresh apple juice", "Mint", "Lemon", "Ice"],
           ar: ["عصير تفاح طازج", "نعناع", "ليمون", "ثلج"]
         }
       },
       "breeze": {
         name: "Apple Breeze",
         price: "30 LE",
         description: "Fresh apple juice with mint and lemon",
         description_ar: "عصير تفاح طازج مع نعناع وليمون",
         category: "beverage",
         ingredients: {
           en: ["Fresh apple juice", "Mint", "Lemon", "Ice"],
           ar: ["عصير تفاح طازج", "نعناع", "ليمون", "ثلج"]
         }
       },
       "frenchman blitz": {
         name: "Frenchman Blitz",
         price: "40 LE",
         description: "French cocktail with fresh fruits and herbs",
         description_ar: "كوكتيل فرنسي مع فواكه طازجة وأعشاب",
         category: "beverage",
         ingredients: {
           en: ["French cocktail", "Fresh fruits", "Herbs", "Spirits"],
           ar: ["كوكتيل فرنسي", "فواكه طازجة", "أعشاب", "مشروبات كحولية"]
         }
       },
       "frenchman": {
         name: "Frenchman Blitz",
         price: "40 LE",
         description: "French cocktail with fresh fruits and herbs",
         description_ar: "كوكتيل فرنسي مع فواكه طازجة وأعشاب",
         category: "beverage",
         ingredients: {
           en: ["French cocktail", "Fresh fruits", "Herbs", "Spirits"],
           ar: ["كوكتيل فرنسي", "فواكه طازجة", "أعشاب", "مشروبات كحولية"]
         }
       },
       "blitz": {
         name: "Frenchman Blitz",
         price: "40 LE",
         description: "French cocktail with fresh fruits and herbs",
         description_ar: "كوكتيل فرنسي مع فواكه طازجة وأعشاب",
         category: "beverage",
         ingredients: {
           en: ["French cocktail", "Fresh fruits", "Herbs", "Spirits"],
           ar: ["كوكتيل فرنسي", "فواكه طازجة", "أعشاب", "مشروبات كحولية"]
         }
       }
    };
    
    const searchName = mealName.toLowerCase().trim();
    return mealDatabase[searchName] || null;
  }

  /**
   * Handle common suggestion questions that users might ask
   */
  handleSuggestionQuestions(userMessage) {
    const lowerMessage = userMessage.toLowerCase();
    
    // Common suggestion questions and their responses
    const suggestionResponses = {
      // Menu-related suggestions
      "show me the menu": {
        en: "🍽️ **Our Menu Categories:**\n\n🍣 **Sushi & Japanese**\n🥩 **Steaks & Grills**\n🍗 **Chicken Dishes**\n🥗 **Appetizers & Starters**\n🥤 **Beverages & Drinks**\n\n💡 **What category interests you most?**",
        ar: "🍽️ **فئات قائمة الطعام:**\n\n🍣 **سوشي وياباني**\n🥩 **ستيك وشواية**\n🍗 **أطباق دجاج**\n🥗 **مقبلات وبدايات**\n🥤 **مشروبات**\n\n💡 **ما الفئة التي تهمك أكثر؟**"
      },
      "what do you have": {
        en: "🍽️ **We offer a variety of delicious dishes:**\n\n🍣 **Sushi:** Dragon Sushi, Creamy Sushi, Salmon Roll\n🥩 **Steaks:** Steak Tartare, Surf & Turf, Wagyu Beef\n🍗 **Chicken:** Creamy Parmesan, Spring Chicken, Chicken Francese\n🥗 **Appetizers:** Tomato Toast, Noodle Soup, Pumpkin Soup\n🥤 **Beverages:** Panthouse Tonic, Apple Breeze, Frenchman Blitz\n\n💡 **What would you like to know more about?**",
        ar: "🍽️ **نقدم مجموعة متنوعة من الأطباق اللذيذة:**\n\n🍣 **سوشي:** سوشي التنين، سوشي كريمي، رول السلمون\n🥩 **ستيك:** ستيك تارتار، سيرف آند تيرف، لحم واجيو\n🍗 **دجاج:** دجاج بارميزان كريمي، دجاج الربيع، دجاج فرانسيس\n🥗 **مقبلات:** توست الطماطم، شوربة النودلز، شوربة القرع\n🥤 **مشروبات:** تونيك بانثوس، نسيم التفاح، بليتز الفرنسي\n\n💡 **ماذا تريد معرفة المزيد عنه؟**"
      },
      "what are your specials": {
        en: "🌟 **Today's Specials:**\n\n🍣 **Dragon Sushi** - Fresh salmon with avocado (120 LE)\n🥩 **Wagyu Beef** - Premium Japanese beef (280 LE)\n🍗 **Creamy Parmesan Chicken** - Tender chicken with parmesan (140 LE)\n🥗 **Pumpkin Soup** - Seasonal favorite (50 LE)\n\n💡 **Would you like to try one of these special dishes?**",
        ar: "🌟 **عروض اليوم:**\n\n🍣 **سوشي التنين** - سلمون طازج مع أفوكادو (120 ج.م)\n🥩 **لحم واجيو** - لحم بقري ياباني فاخر (280 ج.م)\n🍗 **دجاج بارميزان كريمي** - دجاج طري مع بارميزان (140 ج.م)\n🥗 **شوربة القرع** - المفضلة الموسمية (50 ج.م)\n\n💡 **تريد تجربة أحد هذه الأطباق المميزة؟**"
      },
      "what's popular": {
        en: "🔥 **Our Most Popular Dishes:**\n\n🍣 **Dragon Sushi** - Customer favorite (120 LE)\n🥩 **Steak Tartare** - Classic choice (180 LE)\n🍗 **Creamy Parmesan Chicken** - Always requested (140 LE)\n🥗 **Tomato Toast** - Perfect starter (45 LE)\n🥤 **Panthouse Tonic** - Refreshing choice (35 LE)\n\n💡 **Would you like to try one of these popular dishes?**",
        ar: "🔥 **أكثر أطباقنا شعبية:**\n\n🍣 **سوشي التنين** - المفضل لدى العملاء (120 ج.م)\n🥩 **ستيك تارتار** - خيار كلاسيكي (180 ج.م)\n🍗 **دجاج بارميزان كريمي** - مطلوب دائماً (140 ج.م)\n🥗 **توست الطماطم** - مقبلات مثالية (45 ج.م)\n🥤 **تونيك بانثوس** - خيار منعش (35 ج.م)\n\n💡 **تريد تجربة أحد هذه الأطباق الشائعة؟**"
      },
      "how can i order": {
        en: "📱 **How to Order:**\n\n1️⃣ **Visit our restaurant** for dine-in experience\n2️⃣ **Call us** at +20-100-383-4294\n3️⃣ **Email us** at tuscany.house@gmail.com\n4️⃣ **Walk-in** - we welcome spontaneous visits!\n\n💡 **Would you like to make a reservation or see our menu?**",
        ar: "📱 **كيفية الطلب:**\n\n1️⃣ **زيارة المطعم** لتجربة تناول الطعام\n2️⃣ **اتصل بنا** على +20-100-383-4294\n3️⃣ **راسلنا عبر البريد** على tuscany.house@gmail.com\n4️⃣ **زيارة مباشرة** - نرحب بالزيارات التلقائية!\n\n💡 **تريد الحجز أو رؤية قائمة الطعام؟**"
      },
      "what are your hours": {
        en: "🕐 **Opening Hours:**\n\n**Sunday - Thursday:** 12:00 PM - 11:00 PM\n**Friday - Saturday:** 12:00 PM - 12:00 AM\n\n💡 **Would you like to make a reservation or see our menu?**",
        ar: "🕐 **ساعات العمل:**\n\n**الأحد - الخميس:** 12:00 م - 11:00 م\n**الجمعة - السبت:** 12:00 م - 12:00 ص\n\n💡 **تريد الحجز أو رؤية قائمة الطعام؟**"
      },
      "where are you located": {
        en: "📍 **Location:**\n\nWe are located in the heart of the city, easily accessible by public transport and car.\n\n💡 **Would you like to make a reservation or see our menu?**",
        ar: "📍 **الموقع:**\n\nنحن موجودون في قلب المدينة، يمكن الوصول إلينا بسهولة عبر وسائل النقل العام والسيارة.\n\n💡 **تريد الحجز أو رؤية قائمة الطعام؟**"
      }
    };
    
    // Check if user message matches any suggestion question
    for (const [question, response] of Object.entries(suggestionResponses)) {
      if (lowerMessage.includes(question) || question.includes(lowerMessage)) {
        return this.currentLanguage === "ar" ? response.ar : response.en;
      }
    }
    
    return null;
  }

  /**
   * Generate ingredients based on meal category and name
   */
  generateIngredients(meal) {
    // First, determine the category if it's not set
    let category = meal.category;
    if (!category) {
      // Determine category based on meal name
      const mealName = meal.name.toLowerCase();
      if (mealName.includes('sushi') || mealName.includes('salmon') || mealName.includes('dragon')) {
        category = 'sushi';
      } else if (mealName.includes('steak') || mealName.includes('beef') || mealName.includes('wagyu') || mealName.includes('tartare')) {
        category = 'steak';
      } else if (mealName.includes('chicken') || mealName.includes('parmesan') || mealName.includes('francese') || mealName.includes('spring')) {
        category = 'chicken';
      } else if (mealName.includes('soup') || mealName.includes('toast') || mealName.includes('noodle') || mealName.includes('pumpkin')) {
        category = 'appetizer';
      } else if (mealName.includes('tonic') || mealName.includes('breeze') || mealName.includes('blitz') || mealName.includes('apple')) {
        category = 'beverage';
      } else {
        category = 'appetizer'; // default fallback
      }
    }

    const ingredients = {
      sushi: {
        "Dragon Sushi": {
          en: ["Fresh salmon", "Avocado", "Cucumber", "Spicy mayo", "Eel sauce"],
          ar: ["سلمون طازج", "أفوكادو", "خيار", "مايونيز حار", "صلصة الإيل"]
        },
        "Creamy Sushi": {
          en: ["Sushi rice", "Cream cheese", "Fresh vegetables", "Creamy sauce"],
          ar: ["أرز سوشي", "كريمة جبن", "خضروات طازجة", "صلصة كريمية"]
        },
        "Salmon Roll": {
          en: ["Fresh salmon", "Sushi rice", "Nori", "Vegetables"],
          ar: ["سلمون طازج", "أرز سوشي", "نوري", "خضروات"]
        },
        default: {
          en: ["Sushi rice", "Fresh vegetables", "Special sauces"],
          ar: ["أرز سوشي", "خضروات طازجة", "صلصات خاصة"]
        }
      },
      steak: {
        "Steak Tartare": {
          en: ["Premium beef", "Capers", "Onion", "Dijon mustard", "Spices"],
          ar: ["لحم بقري فاخر", "كبر", "بصل", "خردل ديجون", "توابل"]
        },
        "Surf & Turf": {
          en: ["Beef", "Seafood", "Grilled vegetables", "Special sauce"],
          ar: ["لحم بقري", "مأكولات بحرية", "خضروات مشوية", "صلصة خاصة"]
        },
        "Wagyu Beef": {
          en: ["Japanese Wagyu beef", "Sea salt", "Black pepper", "Olive oil"],
          ar: ["لحم واجيو ياباني", "ملح البحر", "فلفل أسود", "زيت الزيتون"]
        },
        default: {
          en: ["High-quality beef", "Fresh spices", "Special sauces"],
          ar: ["لحم بقري عالي الجودة", "توابل طازجة", "صلصات خاصة"]
        }
      },
      chicken: {
        "Creamy Parmesan Chicken": {
          en: ["Tender chicken", "Parmesan cheese", "Cream", "Grilled vegetables"],
          ar: ["دجاج طري", "جبن بارميزان", "كريمة", "خضروات مشوية"]
        },
        "Spring Chicken": {
          en: ["Fresh chicken", "Spring vegetables", "Fresh herbs", "Light sauce"],
          ar: ["دجاج طازج", "خضروات الربيع", "أعشاب طازجة", "صلصة خفيفة"]
        },
        "Chicken Francese": {
          en: ["Chicken", "Lemon", "Olive oil", "French herbs"],
          ar: ["دجاج", "ليمون", "زيت الزيتون", "أعشاب فرنسية"]
        },
        default: {
          en: ["Fresh chicken", "Vegetables", "Fresh spices"],
          ar: ["دجاج طازج", "خضروات", "توابل طازجة"]
        }
      },
      appetizer: {
        "Tomato Toast": {
          en: ["Fresh tomatoes", "Artisan bread", "Herbs", "Olive oil"],
          ar: ["طماطم طازجة", "خبز أرتيزان", "أعشاب", "زيت الزيتون"]
        },
        "Noodle Soup": {
          en: ["Asian noodles", "Vegetables", "Chicken broth", "Asian spices"],
          ar: ["نودلز آسيوية", "خضروات", "مرق دجاج", "توابل آسيوية"]
        },
        "Pumpkin Soup": {
          en: ["Fresh pumpkin", "Cream", "Seasonal spices", "Vegetables"],
          ar: ["قرع طازج", "كريمة", "توابل موسمية", "خضروات"]
        },
        default: {
          en: ["Fresh ingredients", "High-quality spices", "Special sauces"],
          ar: ["مكونات طازجة", "توابل عالية الجودة", "صلصات خاصة"]
        }
      },
      beverage: {
        "Panthouse Tonic": {
          en: ["Tonic", "Lemon", "Lime", "Fresh herbs"],
          ar: ["تونيك", "ليمون", "ليمون حامض", "أعشاب طازجة"]
        },
        "Apple Breeze": {
          en: ["Fresh apple juice", "Mint", "Lemon", "Ice"],
          ar: ["عصير تفاح طازج", "نعناع", "ليمون", "ثلج"]
        },
        "Frenchman Blitz": {
          en: ["French cocktail", "Fresh fruits", "Herbs", "Spirits"],
          ar: ["كوكتيل فرنسي", "فواكه طازجة", "أعشاب", "مشروبات كحولية"]
        },
        default: {
          en: ["Fresh ingredients", "Natural fruits", "Fresh herbs"],
          ar: ["مكونات طازجة", "فواكه طبيعية", "أعشاب طازجة"]
        }
      }
    };

    const categoryIngredients = ingredients[category] || ingredients.appetizer;
    const mealIngredients = categoryIngredients[meal.name] || categoryIngredients.default || { en: [], ar: [] };
    
    // Return ingredients based on current language
    return mealIngredients[this.currentLanguage] || mealIngredients.en || [];
  }

  /**
   * Handle fallback for unexpected or generic questions
   */
  handleFallbackResponse() {
    if (this.currentLanguage === "ar") {
      return "🌟 **عذراً، يبدو أنك تبحث عن شيء غير محدد لمطعم توسكانى هاوس أو أنه عام جداً بالنسبة لي لفهمه.**\n\n💡 **يرجى تقديم المزيد من السياق للرد بشكل أفضل أو يمكنك الاتصال بنا عبر:**\n\n📞 **الهاتف:** +20-100-383-4294\n📧 **البريد الإلكتروني:** tuscany.house@gmail.com";
    }
    return "🌟 **I apologize, it seems like you're looking for something that is not specific to Tuscany House or it's too generic for me to understand.**\n\n💡 **Kindly provide me more context to respond well or you can contact us via:**\n\n📞 **Phone:** +20-100-383-4294\n📧 **Email:** tuscany.house@gmail.com";
  }

  /**
   * Handle sensitive or inappropriate topics
   */
  handleSensitiveTopic() {
    if (this.currentLanguage === "ar") {
      return "أعتذر، لكنني مصمم لمساعدتك في أسئلة متعلقة بمطعم توسكانى هاوس فقط. إذا كان لديك أسئلة حول قائمة الطعام، ساعات العمل، الحجوزات، أو أي شيء آخر متعلق بالمطعم، سأكون سعيداً لمساعدتك!";
    }
    return "I apologize, but I'm designed to help you with Tuscany House restaurant questions only. If you have questions about our menu, hours, reservations, or anything else related to our restaurant, I'd be happy to help!";
  }

  /**
   * Check if message contains sensitive content
   */
  isSensitiveContent(message) {
    const sensitiveKeywords = [
      // English sensitive words
      "politics", "religion", "sex", "drugs", "violence", "hate", "discrimination",
      "racism", "sexism", "homophobia", "transphobia", "extremism", "terrorism",
      // Arabic sensitive words
      "سياسة", "دين", "جنس", "مخدرات", "عنف", "كراهية", "تمييز",
      "عنصرية", "تحيز جنسي", "رهاب المثلية", "إرهاب", "تطرف"
    ];

    const lowerMessage = message.toLowerCase();
    return sensitiveKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  /**
   * Check if user message is about sushi
   */
  isSushiQuery(userMessage) {
    const lowerMessage = userMessage.toLowerCase();
    const sushiKeywords = [
      "sushi", "سوشي", "sashimi", "maki", "roll", "رول"
    ];
    
    return sushiKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  /**
   * Generate sushi response
   */
  generateSushiResponse() {
    if (this.currentLanguage === "ar") {
      return `🍣 **أطباق السوشي لدينا تشمل:**

1. **سوشي التنين** (120 ج.م)
سلمون طازج مع أفوكادو ومايونيز حار

2. **سوشي كريمي** (110 ج.م)
أرز سوشي مع صلصة جبن كريمية

3. **رول السلمون** (115 ج.م)
سلمون طازج مع أرز سوشي ونوري

هل تريد تجربة أحد أطباق السوشي الطازجة لدينا؟`;
    }
    
    return `Our sushi dishes include:

1. **Dragon Sushi** (120 LE)
Fresh salmon with avocado and spicy mayo

2. **Creamy Sushi** (110 LE)
Sushi rice with creamy cheese sauce

3. **Salmon Roll** (115 LE)
Fresh salmon with sushi rice and nori

Would you like to try one of our fresh sushi dishes?`;
  }

  /**
   * Check if message is about booking/reservations
   */
  isBookingQuery(userMessage) {
    const bookingKeywords = [
      // English booking keywords
      "book", "booking", "reserve", "reservation", "table", "reserve a table", "book a table",
      "make a reservation", "make reservation", "how to book", "how to reserve",
      "reservation process", "booking process", "table reservation", "dinner reservation",
      "lunch reservation", "party reservation", "group booking", "private dining",
      // Arabic booking keywords
      "احجز", "حجز", "حجز طاولة", "احجز طاولة", "حجز مائدة", "احجز مائدة",
      "كيف احجز", "كيف أحجز", "عمل حجز", "حجز عشاء", "حجز غداء", "حجز حفلة",
      "حجز مجموعة", "عشاء خاص", "غداء خاص"
    ];

    const lowerMessage = userMessage.toLowerCase();
    return bookingKeywords.some(keyword => lowerMessage.includes(keyword.toLowerCase()));
  }

  /**
   * Generate booking response
   */
  generateBookingResponse() {
    if (this.currentLanguage === "ar") {
      return `🍽️ **حجز الطاولات في مطعم توسكانى هاوس** 🍽️

📞 **للحجز، يمكنك الاتصال بنا عبر:**
• **الهاتف:** +20-100-383-4294
• **البريد الإلكتروني:** tuscany.house@gmail.com

⏰ **ساعات العمل:**
• **الأحد - الخميس:** 12:00 م - 11:00 م
• **الجمعة - السبت:** 12:00 م - 12:00 ص

💡 **معلومات مهمة:**
• نوصي بالحجز المسبق، خاصة في أيام العطلات
• يمكن حجز طاولات للعائلات والمجموعات
• نقدم خدمة العشاء الخاص للمناسبات الخاصة

🎉 **للمناسبات الخاصة والحفلات، يرجى الاتصال بنا مسبقاً للحصول على أفضل خدمة!**

هل تريد معرفة المزيد عن قائمة الطعام أو ساعات العمل؟`;
    }

    return `🍽️ **Table Reservations at Tuscany House** 🍽️

📞 **To make a reservation, you can contact us via:**
• **Phone:** +20-100-383-4294
• **Email:** tuscany.house@gmail.com

⏰ **Opening Hours:**
• **Sunday - Thursday:** 12:00 PM - 11:00 PM
• **Friday - Saturday:** 12:00 PM - 12:00 AM

💡 **Important Information:**
• We recommend advance reservations, especially on holidays
• We can accommodate family and group bookings
• We offer private dining services for special occasions

🎉 **For special events and parties, please contact us in advance for the best service!**

Would you like to know more about our menu or opening hours?`;
  }

  /**
   * Find best matching question from both collections
   */
  findQuestionMatch(userMessage) {
    const lowerMessage = userMessage.toLowerCase();
    
    // Check common questions first
    for (const question of this.commonQuestions) {
      // Check if any keyword matches
      const keywordMatch = question.keywords.some(keyword =>
        lowerMessage.includes(keyword.toLowerCase())
      );

      if (keywordMatch) {
        console.log(`Found common question match: ${question.intent}`);
        return question;
      }
    }

    // Check general questions
    for (const question of this.generalQuestions) {
      // Check if any keyword matches
      const keywordMatch = question.keywords.some(keyword =>
        lowerMessage.includes(keyword.toLowerCase())
      );

      if (keywordMatch) {
        console.log(`Found general question match: ${question.question}`);
        return question;
      }
    }

    return null;
  }

  /**
   * Get response for question (handles both common and general questions)
   */
  getQuestionResponse(question) {
    // Handle common questions (with responses object)
    if (question.responses) {
      if (!question.responses[this.currentLanguage]) {
        return question.responses?.en || question.response || "I'm sorry, I don't have a response for that.";
      }
      const responses = question.responses[this.currentLanguage];
      const randomIndex = Math.floor(Math.random() * responses.length);
      return responses[randomIndex];
    }
    
    // Handle general questions (with answer and answer_ar fields)
    if (this.currentLanguage === "ar" && question.answer_ar) {
      return question.answer_ar;
    }
    
    return question.answer || "I'm sorry, I don't have a response for that.";
  }

  /**
   * Add new common question to Firebase
   */
  async addCommonQuestion(questionData) {
    try {
      const docRef = await addDoc(collection(db, "common_questions"), questionData);
      console.log("Common question added with ID:", docRef.id);
      
      // Refresh local data
      await this.initializeData();
      
      return docRef.id;
    } catch (error) {
      console.error("Error adding common question:", error);
      throw error;
    }
  }

  /**
   * Update existing common question
   */
  async updateCommonQuestion(questionId, updateData) {
    try {
      const questionRef = doc(db, "common_questions", questionId);
      await updateDoc(questionRef, updateData);
      console.log("Common question updated:", questionId);
      
      // Refresh local data
      await this.initializeData();
    } catch (error) {
      console.error("Error updating common question:", error);
      throw error;
    }
  }

  /**
   * Generate response based on user message
   */
  async generateResponse(userMessage) {
    console.log("Enhanced chatbot processing:", userMessage);
    console.log("Current language:", this.currentLanguage);

    // Add typing delay to simulate natural response
    await this.simulateTyping();

    // Check for sensitive content first
    if (this.isSensitiveContent(userMessage)) {
      return this.handleSensitiveTopic();
    }

    // Check for category queries first (chicken, steak, appetizers, etc.)
    const category = this.isCategoryQuery(userMessage);
    if (category) {
      console.log("Found category query:", category);
      return this.generateCategoryResponse(category);
    }

    // Check for specific sushi query
    if (this.isSushiQuery(userMessage)) {
      console.log("Found sushi query");
      return this.generateSushiResponse();
    }

    // Check for ingredient-based queries
    if (this.isIngredientQuery(userMessage)) {
      const ingredient = this.extractIngredient(userMessage);
      if (ingredient) {
        console.log("Found ingredient query:", ingredient);
        const matchingMeals = this.findMealsByIngredient(ingredient);
        return this.generateIngredientResponse(ingredient, matchingMeals);
      }
    }

    // Check for meal-specific questions - Enhanced with comprehensive meal database
    let meal = this.findMealByName(userMessage);
    if (!meal) {
      // Try to find meal in our comprehensive database
      meal = this.getMealInfo(userMessage);
    }
    
    if (meal) {
      console.log("Found meal:", meal.name);
      return this.generateMealResponse(meal);
    }

    // Check for reservation questions first
    const reservationResponse = this.handleReservationQuestions(userMessage);
    if (reservationResponse) {
      console.log("Found reservation question match");
      return reservationResponse;
    }

    // Check for suggestion questions
    const suggestionResponse = this.handleSuggestionQuestions(userMessage);
    if (suggestionResponse) {
      console.log("Found suggestion question match");
      return suggestionResponse;
    }
    
    // Check for questions (both common and general)
    const question = this.findQuestionMatch(userMessage);
    if (question) {
      console.log("Found question match");
      return this.getQuestionResponse(question);
    }

    // Check for booking/reservation queries
    if (this.isBookingQuery(userMessage)) {
      console.log("Found booking query");
      return this.generateBookingResponse();
    }

    // Check for very generic or unclear questions
    const genericKeywords = [
      "what", "how", "why", "when", "where", "who", "which",
      "ماذا", "كيف", "لماذا", "متى", "أين", "من", "أي"
    ];

    const lowerMessage = userMessage.toLowerCase();
    const isGeneric = genericKeywords.some(keyword => 
      lowerMessage.includes(keyword) && lowerMessage.length < 20
    );

    if (isGeneric) {
      return this.handleFallbackResponse();
    }

    // Return null to let other handlers process
    return null;
  }

  /**
   * Simulate typing delay for more natural responses
   */
  async simulateTyping() {
    return new Promise(resolve => {
      setTimeout(() => {
        console.log("🤖 Bot is typing...");
        resolve();
      }, 1500); // 1.5 seconds delay
    });
  }

  /**
   * Handle reservation and booking questions with professional guide
   */
  handleReservationQuestions(userMessage) {
    const lowerMessage = userMessage.toLowerCase();
    
    // Check if message is about reservations/booking
    const reservationKeywords = [
      "reservation", "reserve", "book", "booking", "table", "حجز", "احجز", "طاولة", "حجز طاولة", "احجز طاولة",
      "reserve table", "book table", "make reservation", "table reservation", "حجز مسبق", "حجز طاولة"
    ];
    
    const isReservationQuery = reservationKeywords.some(keyword => 
      lowerMessage.includes(keyword)
    );
    
    if (!isReservationQuery) return null;
    
    // Return the professional reservation guide (Sample 1)
    if (this.currentLanguage === "ar") {
      return `📋 **دليل حجز الطاولة:**

1. 🖱️ اضغط زر "احجز طاولة"
2. 🍽️ اختر: إفطار | غداء | عشاء
3. ✍️ املأ بياناتك
4. 🎯 اختر: طلب مسبق أو طلب عند الوصول
5. 🪑 اختر المقعد: دائري (مقعدان) أو مربع (4 مقاعد)
6. 💰 رسوم الحجز: 70 ج.م
7. 💳 الدفع: بطاقة أو نقداً عند الوصول

مستعد للحجز؟ اضغط زر الحجز أدناه! 🚀`;
    }
    
    return `📋 **Table Reservation Guide:**

1. 🖱️ Click "Book a Table" button
2. 🍽️ Choose: Breakfast | Lunch | Dinner
3. ✍️ Fill in your details
4. 🎯 Select: Pre-order or Order on arrival
5. 🪑 Choose seat: Circle (2 seats) or Square (4 seats)
6. 💰 Reservation fee: 70 LE
7. 💳 Payment: Card or Cash on arrival

Ready to reserve? Click the booking button below! 🚀`;
  }

  /**
   * Refresh data from Firebase
   */
  async refreshData() {
    await this.initializeData();
    console.log("Enhanced chatbot data refreshed");
  }
}

// Create singleton instance
const enhancedChatbotService = new EnhancedChatbotService();

// Export functions for use in components
export async function getEnhancedResponse(userMessage, language = null) {
  if (language) {
    enhancedChatbotService.setLanguage(language);
  }
  return await enhancedChatbotService.generateResponse(userMessage);
}

export function setEnhancedLanguage(language) {
  enhancedChatbotService.setLanguage(language);
}

export async function refreshEnhancedData() {
  await enhancedChatbotService.refreshData();
}

export async function addCommonQuestion(questionData) {
  return await enhancedChatbotService.addCommonQuestion(questionData);
}

export async function updateCommonQuestion(questionId, updateData) {
  return await enhancedChatbotService.updateCommonQuestion(questionId, updateData);
}

export default enhancedChatbotService; 